import { Link, useLocation } from 'react-router-dom'

import { api } from '../api/client'
import type { ConsoleDashboard, DashboardKind, EagleMonitorato } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../components/common'
import { DataTable } from '../components/DataTable'
import { DashboardPersonalizzabile } from '../components/DashboardPersonalizzabile'
import { dashboardPredefinitaEagle } from '../components/widget/modello'
import { EagleNotifiche } from '../components/EagleNotifiche'
import { EagleProfili } from '../components/EagleProfili'
import { EagleReport } from '../components/EagleReport'
import { EagleSonde } from '../components/EagleSonde'
import { GestioneCruscotti } from '../components/GestioneCruscotti'
import { LinkMonitor } from '../components/LinkMonitor'
import { MonitorParete } from '../components/MonitorParete'
import { useAsync } from '../hooks/useAsync'
import { useEagle } from '../hooks/useEagle'
import { t } from '../i18n/lingua'

/**
 * Eagle (ADR-0022), in cinque viste dal menu: il **cruscotto** (`/eagle`), i **CI
 * monitorati** (`/eagle/cis`), le **sonde** (`/eagle/sonde`), le **notifiche**
 * (`/eagle/notifiche`) e i **report** (`/eagle/report`). Il monitoraggio
 * si attiva dalla scheda del CI; qui si vede l'insieme. Senza l'add-on la
 * pagina non esiste (il menu non la mostra, e chi ci arriva da un collegamento
 * vede un avviso).
 */
/** Il nome del modulo: un nome di prodotto, uguale in ogni lingua. */
const NOME = 'Eagle'

/**
 * The address that shows THIS dashboard on the wall: by id when it is saved,
 * as a copy (`bozza=1`, kept in localStorage by `ricordaBozza`) when it is
 * not — the default one, or one being looked at before saving — and with the
 * section open now, so the wall starts where the eyes were.
 */
function versoLaParete(kind: DashboardKind, dash: ConsoleDashboard, sezione: number): string {
  const q = new URLSearchParams({ kind })
  if (dash.id) q.set('d', dash.id)
  else q.set('bozza', '1')
  if (sezione > 0) q.set('sezione', String(sezione))
  return `/eagle/sinottico?${q.toString()}`
}

export function ricordaBozza(kind: DashboardKind, dash: ConsoleDashboard): void {
  if (dash.id) return
  try { localStorage.setItem(`censo.dashboard.bozza.${kind}`, JSON.stringify(dash)) } catch { /* memoria del browser non disponibile */ }
}

export default function Eagle() {
  const attivo = useEagle()
  const { pathname } = useLocation()
  const { principal } = useAuth()
  const amministratore =
    principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin') || false
  const vista = pathname.endsWith('/cis') ? 'cis' : pathname.endsWith('/sonde') ? 'sonde'
    : pathname.endsWith('/notifiche') ? 'notifiche' : pathname.endsWith('/report') ? 'report'
      : pathname.endsWith('/profili') ? 'profili' : pathname.endsWith('/parete') ? 'parete'
        : pathname.endsWith('/cruscotti') ? 'cruscotti' : 'cruscotto'
  const stato = useAsync(() => api.eagleStatus(), [])

  if (stato.loading && !stato.data) return <Spinner />
  if (!attivo && stato.data && !stato.data.licensed) {
    return <EmptyState title={t('Pagina non disponibile')} hint={t('Questa funzione non è attiva per il tuo spazio.')} />
  }
  const sottotitolo = {
    cruscotto: '',
    cis: t('Il monitoraggio si abilita dalla scheda del singolo CI, nella sezione Eagle.'),
    sonde: t('Le sonde viste da Eagle: chi legge che cosa, e con quale carico.'),
    notifiche: t('Gestione delle notifiche: contatti, orari di reperibilità, gruppi di notifica e SLA.'),
    report: t('Crea report configurabili per periodo, CI e contenuti. I report vengono generati sui dati correnti e possono essere esportati in PDF o Excel.'),
    profili: t('Profili di monitoraggio: regole per tipo di parametro da applicare a più CI insieme, o da soli ai CI con un tag.'),
    parete: '',
    cruscotti: t('Tutti i cruscotti di Eagle: aprirli, mandarli a parete, duplicarli, rinominarli, condividerli con il tenant; i monitor si assegnano in «Sinottici pubblicati».'),
  }[vista]
  if (vista === 'parete') {
    // A published wall display is a cockpit assigned to a monitor: this page
    // lists the monitors (the links) and what each one shows. The cockpits
    // themselves live in «Cruscotto» and «Gestione cruscotti».
    return (
      <>
        <header className="page__header">
          <div>
            <h1 className="page__title">{NOME}<span className="muted"> · {t('Sinottici pubblicati')}</span></h1>
            <p className="page__subtitle">{t('I monitor a parete: ogni sinottico pubblicato è un cruscotto assegnato a un monitor. Con CENSO Monitor sul PC dello schermo lo accoppi con un codice e lo comandi da qui; in alternativa un link che si apre senza accesso. Il cruscotto si compone in «Cruscotto» e si manda a parete con «Mostra a parete».')}</p>
          </div>
          <Link to="/eagle/sinottico?kind=eagle" className="eagle-sinottico-link" style={{ alignSelf: 'center' }}>{t('Apri il sinottico')}</Link>
        </header>
        {amministratore ? <><MonitorParete /><LinkMonitor /></> : (
          <EmptyState title={t('Solo gli amministratori assegnano i cruscotti ai monitor.')} hint={t('Chiedi a un amministratore del tenant di creare il link per il monitor.')} />
        )}
      </>
    )
  }
  if (vista === 'cruscotto') {
    return (
      <DashboardPersonalizzabile
        kind="eagle"
        gruppi={['eagle', 'generale']}
        predefinita={dashboardPredefinitaEagle}
        titolo={(dash, modifica, cambiaNome) => modifica ? (
          <input value={dash.name} onChange={(e) => cambiaNome(e.target.value)} aria-label={t('Nome della dashboard')} style={{ fontSize: 20, fontWeight: 600, maxWidth: 360 }} />
        ) : <>{NOME}<span className="muted"> · {dash.name}</span></>}
        sottotitolo={(dash) => dash.shared ? t('Cruscotto del tenant') : dash.id ? t('Il tuo cruscotto') : t('Stato operativo degli oggetti monitorati, configurabile in base alle specifiche esigenze.')}
        // «Mostra a parete» shows THIS cockpit full screen (the one on screen,
        // saved or not), not the wall display composed in Eagle → Sinottico.
        azioni={(dash, sezione) => <Link to={versoLaParete('eagle', dash, sezione)} onClick={() => ricordaBozza('eagle', dash)} className="eagle-sinottico-link" style={{ alignSelf: 'center' }}>{t('Mostra a parete')}</Link>}
      />
    )
  }
  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">
            {NOME}
            {vista === 'cis' && <span className="muted"> · {t('CI monitorati')}</span>}
            {vista === 'sonde' && <span className="muted"> · {t('Sonde')}</span>}
            {vista === 'notifiche' && <span className="muted"> · {t('Notifiche')}</span>}
            {vista === 'report' && <span className="muted"> · {t('Report')}</span>}
            {vista === 'profili' && <span className="muted"> · {t('Profili')}</span>}
            {vista === 'cruscotti' && <span className="muted"> · {t('Gestione cruscotti')}</span>}
          </h1>
          <p className="page__subtitle">{sottotitolo}</p>
        </div>
      </header>
      {vista === 'cis' && <CIMonitorati />}
      {vista === 'sonde' && <EagleSonde puoModificare={amministratore} />}
      {vista === 'notifiche' && <EagleNotifiche puoModificare={amministratore || (principal?.roles.includes('operator') ?? false)} />}
      {vista === 'report' && <EagleReport puoModificare={amministratore || (principal?.roles.includes('operator') ?? false)} />}
      {vista === 'profili' && <EagleProfili puoModificare={amministratore || (principal?.roles.includes('operator') ?? false)} />}
      {vista === 'cruscotti' && <GestioneCruscotti amministratore={amministratore} />}
    </>
  )
}

function CIMonitorati() {
  const dati = useAsync(() => api.eagleCis(), [])
  if (dati.loading && !dati.data) return <Spinner />
  const righe = dati.data?.cis ?? []
  return (
    <>
      <ErrorBanner error={dati.error} />
      {righe.length === 0 ? (
        <EmptyState
          title={t('Nessun CI monitorato')}
          hint={t('Apri un CI in Inventario → Configuration Item, vai alla sezione Eagle e scegli il profilo predefinito o i parametri da monitorare.')}
        />
      ) : (
        <Card title={t('CI monitorati ({n})', { n: righe.length })}>
          <DataTable<EagleMonitorato>
            rows={righe}
            rowKey={(r) => r.ci_id}
            initialSort={{ key: 'name' }}
            columns={[
              { key: 'name', header: t('CI'), value: (r) => r.name, render: (r) => <Link to={`/cis/${r.ci_id}#eagle`}>{r.name}</Link> },
              { key: 'ci_class', header: t('Classe'), value: (r) => r.ci_class ?? '' },
              { key: 'mgmt_ip', header: t('Indirizzo'), mono: true, value: (r) => r.mgmt_ip ?? '' },
              { key: 'mode', header: t('Attivazione'), value: (r) => (r.mode === 'profile' ? t('Profilo predefinito') : t('Scelta manuale')) },
              { key: 'params', header: t('Parametri'), value: (r) => r.params },
              { key: 'activated_at', header: t('Attivo dal'), value: (r) => r.activated_at ?? '', render: (r) => formatDateTime(r.activated_at) },
            ]}
          />
        </Card>
      )}
    </>
  )
}
